import type { ComponentType } from 'react';
import type { GetServerSideProps, NextPage } from 'next';
import dynamic from 'next/dynamic';
import type { ParsedUrlQuery } from 'querystring';
import { checkUserAuthOnServer } from '@mwl/core-lib';
import { defaultLocale } from '@/constants';
import type { CasinoLayoutProps } from '@/Layouts/CasinoLayout/CasinoLayout.types';
import type { CasinoCategoryPageTypes, CategoryPageProps } from '@/modules/casino/types/categoryPage';
import { loadCategoryData } from '@/modules/casino/utils/loadCategoryData';
import { wrapperStore } from '@/store/store';
const CasinoLayout = dynamic(() => import('@/Layouts/CasinoLayout/CasinoLayout').then(mod => mod.CasinoLayout));
const CasinoRoulettePage = dynamic(() => import('@/modules/casino/pages/CasinoRoulettePage/CasinoRoulettePage').then(mod => mod.CasinoRoulettePage));
const CasinoCrashGamesPage = dynamic(() => import('@/modules/casino/pages/CasinoCrashGamesPage/CasinoCrashGamesPage').then(mod => mod.CasinoCrashGamesPage));
const CasinoAllGamesPage = dynamic(() => import('@/modules/casino/pages/CasinoAllGamesPage/CasinoAllGamesPage').then(mod => mod.CasinoAllGamesPage));
const CasinoTopGamesPage = dynamic(() => import('@/modules/casino/pages/CasinoTopGamesPage/CasinoTopGamesPage').then(mod => mod.CasinoTopGamesPage));
const CasinoCardsPage = dynamic(() => import('@/modules/casino/pages/CasinoCardsPage/CasinoCardsPage').then(mod => mod.CasinoCardsPage));
const CasinoBuyBonusPage = dynamic(() => import('@/modules/casino/pages/CasinoBuyBonusPage/CasinoBuyBonusPage').then(mod => mod.CasinoBuyBonusPage));
const CasinoSlotsPage = dynamic(() => import('@/modules/casino/pages/CasinoSlotsPage/CasinoSlotsPage').then(mod => mod.CasinoSlotsPage));
const CasinoFastGamesPage = dynamic(() => import('@/modules/casino/pages/CasinoFastGamesPage/CasinoFastGamesPage').then(mod => mod.CasinoFastGamesPage));
const CasinoTableGamesPage = dynamic(() => import('@/modules/casino/pages/CasinoTableGamesPage/CasinoTableGamesPage').then(mod => mod.CasinoTableGamesPage));
const CasinoJackpotsPage = dynamic(() => import('@/modules/casino/pages/CasinoJackpotsPage/CasinoJackpotsPage').then(mod => mod.CasinoJackpotsPage));
const CasinoBrandedPage = dynamic(() => import('@/modules/casino/pages/CasinoBrandedPage/CasinoBrandedPage').then(mod => mod.CasinoBrandedPage));
const CasinoRecommendationsGamesPage = dynamic(() => import('@/modules/casino/pages/CasinoRecommendationsGamesPage/CasinoRecommendationsGamesPage').then(mod => mod.CasinoRecommendationsGamesPage));
const CasinoNewGamesPage = dynamic(() => import('@/modules/casino/pages/CasinoNewGamesPage/CasinoNewGamesPage').then(mod => mod.CasinoNewGamesPage));
interface CasinoCategoryPageProps extends CategoryPageProps, Pick<CasinoLayoutProps, 'banners'> {
  category: CasinoCategoryPageTypes;
}
interface Params extends ParsedUrlQuery {
  category: CasinoCategoryPageTypes;
}
const gameMode: Record<CasinoCategoryPageTypes, ComponentType<CategoryPageProps>> = {
  all: CasinoAllGamesPage,
  top: CasinoTopGamesPage,
  recommendations: CasinoRecommendationsGamesPage,
  slots: CasinoSlotsPage,
  roulette: CasinoRoulettePage,
  cards: CasinoCardsPage,
  fast_games: CasinoFastGamesPage,
  table_games: CasinoTableGamesPage,
  bonus_buy: CasinoBuyBonusPage,
  jackpots: CasinoJackpotsPage,
  crash_games: CasinoCrashGamesPage,
  banzai_games: CasinoBrandedPage,
  new: CasinoNewGamesPage
};
const CasinoCategoryPage: NextPage<CasinoCategoryPageProps> = ({
  category,
  banners,
  initialData
}) => {
  const CategoryPage = gameMode[category];
  return <CasinoLayout banners={banners} data-sentry-element="CasinoLayout" data-sentry-component="CasinoCategoryPage" data-sentry-source-file="index.tsx">
      <CategoryPage initialData={initialData} data-sentry-element="CategoryPage" data-sentry-source-file="index.tsx" />
    </CasinoLayout>;
};
export const getServerSideProps: GetServerSideProps<CasinoCategoryPageProps, Params> = wrapperStore.getServerSideProps(({
  dispatch
}) => async ({
  locale = defaultLocale,
  req,
  res,
  query
}) => {
  const {
    category
  } = (query as Params);
  if (!gameMode[category]) {
    return {
      notFound: true,
      props: ({} as CasinoCategoryPageProps)
    };
  }
  const {
    localServerAxiosInstance
  } = await checkUserAuthOnServer({
    dispatch,
    locale,
    res,
    req
  });
  const {
    translations,
    banners,
    initialData
  } = await loadCategoryData({
    locale,
    req,
    dispatch,
    category,
    axiosInstance: localServerAxiosInstance
  });
  return {
    props: {
      category,
      ...translations,
      ...banners,
      initialData
    }
  };
});
export default CasinoCategoryPage;